import { useCallback } from 'react';
import { push } from 'redux-first-router';
import { RouterLink } from '@redux-first-router-link';
import track, { useTracking } from 'react-tracking';

import { Grid, Text, Box } from '@simplywallst/ui-core';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { ROUTE_ARTICLE } from '@/constants/routes';
import { getPath } from '@/router/utils';

import { useIsMobileScreen } from '@/hooks/useMedia';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';

import { ArticleThumbnail } from './styled';

type Props = {
  title: string;
  imageUrl: string | null;
  slug: string;
};

const ArticleFeedItem = ({ imageUrl, title, slug }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { trackEvent } = useTracking();
  const coverImageSrc = useCloudflareImage({
    src: imageUrl,
    width: 56,
    height: 36,
    fit: 'cover',
  });

  const canonicalUrl = getPath({ type: ROUTE_ARTICLE, payload: { slug } });
  const clickOnCard = useCallback(() => {
    push(canonicalUrl);
    trackEvent({
      modifier: 'last-week-market-article',
      action: 'click',
      slug,
    });
  }, [slug]);

  return (
    <Box
      paddingBottom="x2"
      paddingTop="x2"
      paddingRight="x3"
      paddingLeft="x3"
      backgroundColor={isMobileScreen ? 'spaceGray' : 'noir'}
      borderRadius="x1"
      onClick={clickOnCard}
    >
      <Grid gap="10px" templateRows="auto" templateColumns="70px auto">
        <Grid>
          <RouterLink
            noUnderline
            to={{
              type: ROUTE_ARTICLE,
              payload: { slug },
            }}
            onClick={() => {
              trackEvent({
                modifier: 'last-week-market-article',
                action: 'click',
                slug,
              });
            }}
          >
            <ArticleThumbnail
              src={coverImageSrc}
              width={56}
              height={36}
              alt={title}
            />
          </RouterLink>
        </Grid>
        <Grid alignContent="start">
          <Text as="span" typography="tiny" color="whiteRGBA" opacity={0.5}>
            Last Week
          </Text>
          <RouterLink
            noUnderline
            to={{
              type: ROUTE_ARTICLE,
              payload: { slug },
            }}
            onClick={() => {
              trackEvent({
                modifier: 'market-latest-article',
                action: 'click',
                slug,
              });
            }}
          >
            <Text typography="xSmallStrong">{title}</Text>
          </RouterLink>
        </Grid>
      </Grid>
    </Box>
  );
};

export default track({
  type: 'track',
  subject: 'ArticleFeedItem',
})(ArticleFeedItem) as typeof ArticleFeedItem;
